import {
    PosmClosureStatus,
    PosmCompletionReport,
    PosmCostingSummary,
    PosmGenerateCompletionReportRequest,
    PosmReportAvailability,
    PosmReportExport
} from '../../posm/models/posm-project.model';
import { PosmExecutionReport, PosmScheduleCompletionReport } from '../../posm/models/posm-execution.models';
import { AuditActorSummary } from '../../shared/audit/audit.models';

export interface LogisticsProgramSummary {
    programId?: number;
    programNumber?: string;
    programName?: string;
    status?: string;
}

export interface LogisticsProgram {
    programId?: number;
    programNumber?: string;
    owningCompanyId?: number;
    programName?: string;
    programType?: string;
    status?: string;
    priority?: string;
    customerCompanyId?: number;
    crmClientId?: number;
    crmAccountId?: number;
    crmClientContactId?: number;
    contractId?: number;
    salesPersonId?: number;
    programManagerId?: number;
    defaultCurrencyCode?: string;
    plannedStartAt?: Date;
    plannedEndAt?: Date;
    actualStartAt?: Date;
    actualEndAt?: Date;
    description?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface LogisticsProject {
    projectId?: number;
    projectNumber?: string;
    owningCompanyId?: number;
    sharingMode?: 'PRIVATE' | 'GROUP';
    program?: LogisticsProgramSummary;
    projectName?: string;
    projectType?: string;
    projectCategory?: string;
    serviceProduct?: string;
    operationalMode?: string;
    status?: string;
    lifecycleStage?: string;
    priority?: string;
    healthStatus?: string;
    customerCompanyId?: number;
    crmClientId?: number;
    crmAccountId?: number;
    crmClientContactId?: number;
    customerContactId?: number;
    contactName?: string;
    contactPhone?: string;
    contactEmail?: string;
    salesPersonId?: number;
    projectManagerId?: number;
    supervisorId?: number;
    departmentId?: number;
    defaultCostCenterId?: number;
    defaultWarehouseId?: number;
    defaultCurrencyCode?: string;
    transportServiceContractId?: number;
    sourceQuotationId?: number;
    sourceSalesOrderId?: number;
    sourcePurchaseOrderId?: number;
    originCountryId?: number;
    sourceLocationId?: number;
    destinationLocationId?: number;
    projectLocationText?: string;
    laneCode?: string;
    plannedStartAt?: Date;
    plannedEndAt?: Date;
    actualStartAt?: Date;
    actualEndAt?: Date;
    baselineStartAt?: Date;
    baselineEndAt?: Date;
    completionMethod?: string;
    percentComplete?: number;
    collectProgress?: boolean;
    estimatedRevenueAmount?: number;
    estimatedCostAmount?: number;
    approvedBudgetAmount?: number;
    budgetVersion?: number;
    billingPolicy?: string;
    revenueRecognitionPolicy?: string;
    costingPolicy?: string;
    publicAccessStatus?: string;
    description?: string;
    notes?: string;
    closureStatus?: string;
    closureReason?: string;
    closureComment?: string;
    closedAt?: Date;
    closedByCustomerId?: number;
    closedByActor?: AuditActorSummary;
    reopenedAt?: Date;
    reopenedByCustomerId?: number;
    reopenedByActor?: AuditActorSummary;
    legacySourceModule?: string;
    legacySourceId?: number;
    createdBy?: number;
    updatedBy?: number;
    createdAt?: Date;
    updatedAt?: Date;
    createdByActor?: AuditActorSummary;
    updatedByActor?: AuditActorSummary;
}

export interface LogisticsProjectSummary {
    projectId?: number;
    owningCompanyId?: number;
    sharingMode?: 'PRIVATE' | 'GROUP';
    projectNumber?: string;
    projectName?: string;
    projectType?: string;
    status?: string;
    healthStatus?: string;
    priority?: string;
    lifecycleStage?: string;
    customerCompanyName?: string;
    programNumber?: string;
    programName?: string;
    projectManagerName?: string;
    supervisorName?: string;
    plannedStartAt?: Date;
    plannedEndAt?: Date;
    percentComplete?: number;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface StatusChangeRequest {
    newStatus?: string;
    reason?: string;
    comment?: string;
    overrideClosure?: boolean;
    overrideReason?: string;
}

export interface ProjectTemplateMilestone {
    templateMilestoneId?: number;
    templateId?: number;
    milestoneCode?: string;
    milestoneName?: string;
    description?: string;
    sequenceNo?: number;
    plannedOffsetDays?: number;
    plannedDurationDays?: number;
    weight?: number;
    autoEventCode?: string;
    isRequired?: boolean;
}

export interface ProjectTemplateRequirementLine {
    templateRequirementId?: number;
    templateId?: number;
    requirementCode?: string;
    requirementName?: string;
    requirementType?: string;
    targetResourceType?: string;
    unitCode?: string;
    defaultQuantity?: number;
    originLocationText?: string;
    destinationLocationText?: string;
    serviceProduct?: string;
    plannedOffsetDays?: number;
    plannedDurationDays?: number;
    priority?: string;
    sourcingMode?: string;
    unitRevenueRate?: number;
    unitCostRate?: number;
    weight?: number;
    sortOrder?: number;
    isActive?: boolean;
    notes?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectTemplate {
    templateId?: number;
    owningCompanyId?: number;
    templateCode?: string;
    templateName?: string;
    projectType?: string;
    projectCategory?: string;
    serviceProduct?: string;
    operationalMode?: string;
    completionMethod?: string;
    defaultPriority?: string;
    defaultBillingPolicy?: string;
    defaultRevenueRecognitionPolicy?: string;
    defaultCostingPolicy?: string;
    description?: string;
    isActive?: boolean;
    createdAt?: Date;
    updatedAt?: Date;
    milestones?: ProjectTemplateMilestone[];
    requirements?: ProjectTemplateRequirementLine[];
}

export interface ProjectMilestone {
    milestoneId?: number;
    owningCompanyId?: number;
    projectId?: number;
    templateId?: number;
    templateCode?: string;
    milestoneCode?: string;
    milestoneName?: string;
    description?: string;
    sequenceNo?: number;
    plannedStartAt?: Date;
    plannedEndAt?: Date;
    actualCompletedAt?: Date;
    status?: string;
    weight?: number;
    percentComplete?: number;
    completionEventCode?: string;
    sourceEventId?: number;
    completionNotes?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectWorkPackage {
    workPackageId?: number;
    owningCompanyId?: number;
    projectId?: number;
    milestoneId?: number;
    packageCode?: string;
    packageName?: string;
    description?: string;
    status?: string;
    weight?: number;
    percentComplete?: number;
    sourceModule?: string;
    sourceResourceType?: string;
    sourceResourceId?: number;
    plannedStartAt?: Date;
    plannedEndAt?: Date;
    actualStartAt?: Date;
    actualEndAt?: Date;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectPlanSummary {
    progressPercent?: number;
    taskCount?: number;
    milestoneCount?: number;
    overdueTaskCount?: number;
    criticalTaskCount?: number;
    completedTaskCount?: number;
    baselineVarianceDays?: number;
    planStartAt?: Date;
    planEndAt?: Date;
}

export interface ProjectPlanTask {
    planTaskId?: number;
    owningCompanyId?: number;
    projectId?: number;
    parentTaskId?: number;
    milestoneId?: number;
    workPackageId?: number;
    taskCode?: string;
    taskName?: string;
    description?: string;
    wbs?: string;
    sequencePath?: string;
    outlineLevel?: number;
    sortOrder?: number;
    taskType?: string;
    schedulingMode?: string;
    durationDays?: number;
    plannedStartAt?: Date;
    plannedEndAt?: Date;
    baselineStartAt?: Date;
    baselineEndAt?: Date;
    actualStartAt?: Date;
    actualEndAt?: Date;
    percentComplete?: number;
    status?: string;
    priority?: string;
    constraintType?: string;
    constraintDate?: Date;
    critical?: boolean;
    slackDays?: number;
    completionEventCode?: string;
    sourceEventId?: number;
    sourceModule?: string;
    sourceResourceType?: string;
    sourceResourceId?: number;
    notes?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectPlanDependency {
    dependencyId?: number;
    owningCompanyId?: number;
    projectId?: number;
    predecessorTaskId?: number;
    successorTaskId?: number;
    dependencyType?: string;
    lagDays?: number;
    createdAt?: Date;
}

export interface ProjectPlanAssignment {
    assignmentId?: number;
    owningCompanyId?: number;
    projectId?: number;
    planTaskId?: number;
    assignmentType?: string;
    assignedCustomerId?: number;
    employeeId?: number;
    assignedEmployeeId?: number;
    participantId?: number;
    assignedRole?: string;
    assignedResourceType?: string;
    assignedResourceId?: number;
    resourceLabel?: string;
    allocationPercent?: number;
    plannedHours?: number;
    actualHours?: number;
    productivityTaskId?: number;
    syncStatus?: string;
    status?: string;
    notes?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectPlanBaselineTask {
    baselineTaskId?: number;
    baselineId?: number;
    planTaskId?: number;
    taskName?: string;
    wbs?: string;
    plannedStartAt?: Date;
    plannedEndAt?: Date;
    durationDays?: number;
    percentComplete?: number;
    varianceDays?: number;
}

export interface ProjectPlanBaseline {
    baselineId?: number;
    owningCompanyId?: number;
    projectId?: number;
    baselineName?: string;
    baselineCode?: string;
    primary?: boolean;
    capturedAt?: Date;
    capturedByCustomerId?: number;
    notes?: string;
    tasks?: ProjectPlanBaselineTask[];
}

export interface ProjectPlanWorkspace {
    projectId?: number;
    owningCompanyId?: number;
    summary?: ProjectPlanSummary;
    tasks?: ProjectPlanTask[];
    dependencies?: ProjectPlanDependency[];
    assignments?: ProjectPlanAssignment[];
    baselines?: ProjectPlanBaseline[];
    primaryBaseline?: ProjectPlanBaseline;
    readOnly?: boolean;
}

export interface ProjectPlanBulkTaskUpdate {
    tasks?: ProjectPlanTask[];
}

export interface ProjectPlanBaselineRequest {
    baselineName?: string;
    baselineCode?: string;
    primary?: boolean;
    notes?: string;
}

export interface ProjectRequirementLine {
    requirementId?: number;
    owningCompanyId?: number;
    projectId?: number;
    templateRequirementId?: number;
    requirementCode?: string;
    requirementName?: string;
    requirementType?: string;
    targetResourceType?: string;
    targetSourceModule?: string;
    unitCode?: string;
    requiredQuantity?: number;
    fulfilledQuantity?: number;
    remainingQuantity?: number;
    percentComplete?: number;
    originLocationText?: string;
    destinationLocationText?: string;
    laneCode?: string;
    serviceProduct?: string;
    sourceQuotationInstallationRowId?: number;
    plannedStartAt?: Date;
    plannedEndAt?: Date;
    priority?: string;
    status?: string;
    sourcingMode?: string;
    allocationStatus?: string;
    generationStatus?: string;
    generationBatchId?: string;
    unitRevenueRate?: number;
    unitCostRate?: number;
    plannedRevenueAmount?: number;
    plannedCostAmount?: number;
    currencyCode?: string;
    weight?: number;
    sortOrder?: number;
    notes?: string;
    metadataJson?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectRequirementAllocation {
    allocationId?: number;
    owningCompanyId?: number;
    projectId?: number;
    requirementId?: number;
    allocationType?: string;
    resourceType?: string;
    resourceId?: number;
    resourceLabel?: string;
    crmClientId?: number;
    supplierName?: string;
    allocatedQuantity?: number;
    unitCostRate?: number;
    plannedCostAmount?: number;
    currencyCode?: string;
    reservedFrom?: Date;
    reservedTo?: Date;
    status?: string;
    notes?: string;
    metadataJson?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectRequirementFulfillmentLink {
    fulfillmentLinkId?: number;
    owningCompanyId?: number;
    projectId?: number;
    requirementId?: number;
    targetResourceType?: string;
    targetResourceId?: number;
    sourceModule?: string;
    sourceReference?: string;
    fulfilledQuantity?: number;
    fulfillmentStatus?: string;
    fulfilledAt?: Date;
    status?: string;
    notes?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectRequirementsSummary {
    owningCompanyId?: number;
    projectId?: number;
    requiredQuantity?: number;
    fulfilledQuantity?: number;
    remainingQuantity?: number;
    allocationCoveragePercent?: number;
    plannedRevenueAmount?: number;
    plannedCostAmount?: number;
    openRequirements?: number;
    completedRequirements?: number;
    exceptionCount?: number;
    lines?: ProjectRequirementLine[];
    allocations?: ProjectRequirementAllocation[];
    fulfillmentLinks?: ProjectRequirementFulfillmentLink[];
}

export interface ProjectRequirementGenerationResponse {
    requirementId?: number;
    targetResourceType?: string;
    generationStatus?: string;
    generationBatchId?: string;
    message?: string;
    requirement?: ProjectRequirementLine;
}

export interface ProjectParticipant {
    participantId?: number;
    owningCompanyId?: number;
    projectId?: number;
    participantType?: string;
    participantRole?: string;
    companyId?: number;
    customerId?: number;
    contactName?: string;
    contactEmail?: string;
    contactPhone?: string;
    visibilityLevel?: string;
    canViewTimeline?: boolean;
    canViewDocuments?: boolean;
    canViewFinancials?: boolean;
    canComment?: boolean;
    status?: string;
    invitedAt?: Date;
    acceptedAt?: Date;
    revokedAt?: Date;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectDocumentRequirement {
    requirementId?: number;
    owningCompanyId?: number;
    projectType?: string;
    documentCode?: string;
    documentName?: string;
    documentType?: string;
    required?: boolean;
    defaultVisibility?: string;
    sortOrder?: number;
    active?: boolean;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectDocument {
    documentId?: number;
    owningCompanyId?: number;
    projectId?: number;
    requirementId?: number;
    documentCode?: string;
    documentName?: string;
    documentType?: string;
    required?: boolean;
    status?: string;
    visibility?: string;
    fileUrl?: string;
    fileName?: string;
    contentType?: string;
    fileSize?: number;
    uploadedAt?: Date;
    uploadedBy?: number;
    verifiedAt?: Date;
    verifiedBy?: number;
    dueDate?: Date;
    notes?: string;
    sourceModule?: string;
    sourceResourceType?: string;
    sourceResourceId?: number;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectComment {
    commentId?: number;
    owningCompanyId?: number;
    projectId?: number;
    visibility?: string;
    commentType?: string;
    message?: string;
    authorCustomerId?: number;
    authorName?: string;
    sourceModule?: string;
    sourceResourceType?: string;
    sourceResourceId?: number;
    parentCommentId?: number;
    status?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectCustomerView {
    project?: LogisticsProject;
    milestones?: ProjectMilestone[];
    documents?: ProjectDocument[];
    updates?: ProjectComment[];
    openBlockers?: string[];
}

export interface CfExecutionFile {
    cfExecutionId?: number;
    owningCompanyId?: number;
    logisticsProjectId?: number;
    cfNumber?: string;
    serviceScope?: string;
    shipmentMode?: string;
    importExportType?: string;
    blAwbNumber?: string;
    hblHawbNumber?: string;
    vesselOrFlight?: string;
    carrier?: string;
    originPort?: string;
    destinationPort?: string;
    etd?: Date;
    eta?: Date;
    ata?: Date;
    deliveryOrderNumber?: string;
    bayanNumber?: string;
    undertakingNumber?: string;
    brokerPartyId?: number;
    transporterPartyId?: number;
    cargoDescription?: string;
    deliveryAddress?: string;
    executionStatus?: string;
    closedAt?: Date;
    closedByCustomerId?: number;
    reopenedAt?: Date;
    reopenedByCustomerId?: number;
    closeReason?: string;
    closeComment?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface CfExecutionResourceSummary {
    resourceType?: string;
    resourceId?: number;
    resourceNumber?: string;
    status?: string;
    primaryReference?: string;
    secondaryReference?: string;
    routeLabel?: string;
    scheduledAt?: Date;
}

export interface CfExecutionActivity {
    activityId?: number;
    owningCompanyId?: number;
    logisticsProjectId?: number;
    cfExecutionId?: number;
    templateLineId?: number;
    activityCode?: string;
    activityName?: string;
    autoTaskNumber?: string;
    milestone?: boolean;
    finalActivity?: boolean;
    allowManualAdd?: boolean;
    sequenceNo?: number;
    defaultSlaHours?: number;
    ownerRole?: string;
    requiredDocumentCodes?: string;
    completionEventCode?: string;
    statusCodeAfterCompletion?: string;
    plannedAt?: Date;
    dueAt?: Date;
    startedAt?: Date;
    completedAt?: Date;
    status?: string;
    sourceEventId?: number;
    completionNotes?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface CfCostElementCatalog {
    catalogId?: number;
    owningCompanyId?: number;
    elementCode?: string;
    elementName?: string;
    category?: string;
    serviceScope?: string;
    shipmentMode?: string;
    buyEnabled?: boolean;
    sellEnabled?: boolean;
    defaultTaxCode?: string;
    defaultTaxRate?: number;
    revenueAccountId?: number;
    expenseAccountId?: number;
    marginThresholdPercent?: number;
    defaultSupplierPartyId?: number;
    defaultCustomerPartyId?: number;
    supplierRequired?: boolean;
    customerRequired?: boolean;
    active?: boolean;
    sortOrder?: number;
}

export interface CfJobCostElement {
    costLineId?: number;
    owningCompanyId?: number;
    logisticsProjectId?: number;
    cfExecutionId?: number;
    catalogId?: number;
    elementCode?: string;
    elementName?: string;
    category?: string;
    serviceScope?: string;
    shipmentMode?: string;
    supplierPartyId?: number;
    customerPartyId?: number;
    buyAmount?: number;
    sellAmount?: number;
    taxAmount?: number;
    currencyCode?: string;
    taxCode?: string;
    marginThresholdPercent?: number;
    quotationLineId?: number;
    salesOrderLineId?: number;
    purchaseOrderLineId?: number;
    clearanceDetailId?: number;
    shipmentChargeLineId?: number;
    invoiceLineId?: number;
    billLineId?: number;
    voucherAllocationId?: number;
    receiptAllocationId?: number;
    financialStage?: string;
    status?: string;
    notes?: string;
    grossMargin?: number;
    grossMarginPercent?: number;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface CfCostWarning {
    code?: string;
    severity?: string;
    message?: string;
    costLineId?: number;
    elementCode?: string;
}

export interface CfCostSheet {
    owningCompanyId?: number;
    logisticsProjectId?: number;
    cfExecutionId?: number;
    cfNumber?: string;
    currencyCode?: string;
    totalBuy?: number;
    totalSell?: number;
    totalTax?: number;
    grossMargin?: number;
    grossMarginPercent?: number;
    catalog?: CfCostElementCatalog[];
    lines?: CfJobCostElement[];
    warnings?: CfCostWarning[];
}

export interface CfSupplierBookingRequestLine {
    lineId?: number;
    requestId?: number;
    costLineId?: number;
    elementCode?: string;
    elementName?: string;
    description?: string;
    quantity?: number;
    unitCode?: string;
    unitAmount?: number;
    taxAmount?: number;
    totalAmount?: number;
    currencyCode?: string;
    billLineId?: number;
    notes?: string;
}

export interface CfSupplierBookingRequest {
    requestId?: number;
    owningCompanyId?: number;
    logisticsProjectId?: number;
    cfExecutionId?: number;
    requestNumber?: string;
    requestType?: string;
    status?: string;
    supplierPartyId?: number;
    supplierName?: string;
    currencyCode?: string;
    requestedAmount?: number;
    approvedAmount?: number;
    taxAmount?: number;
    vendorInvoiceNumber?: string;
    vendorInvoiceDate?: Date;
    dueAt?: Date;
    linkedBillId?: number;
    submittedAt?: Date;
    submittedBy?: number;
    submittedByActor?: AuditActorSummary;
    approvedAt?: Date;
    approvedBy?: number;
    approvedByActor?: AuditActorSummary;
    rejectedAt?: Date;
    rejectedBy?: number;
    rejectedByActor?: AuditActorSummary;
    cancelledAt?: Date;
    cancelledBy?: number;
    cancelledByActor?: AuditActorSummary;
    convertedAt?: Date;
    convertedBy?: number;
    convertedByActor?: AuditActorSummary;
    decisionReason?: string;
    notes?: string;
    createdAt?: Date;
    updatedAt?: Date;
    lines?: CfSupplierBookingRequestLine[];
}

export interface CfShippingPaymentRequestLine {
    lineId?: number;
    paymentRequestId?: number;
    supplierBookingRequestId?: number;
    costLineId?: number;
    elementCode?: string;
    elementName?: string;
    description?: string;
    quantity?: number;
    unitCode?: string;
    unitAmount?: number;
    taxAmount?: number;
    totalAmount?: number;
    currencyCode?: string;
    voucherAllocationId?: number;
    notes?: string;
}

export interface CfShippingPaymentRequest {
    paymentRequestId?: number;
    owningCompanyId?: number;
    logisticsProjectId?: number;
    cfExecutionId?: number;
    requestNumber?: string;
    paymentType?: string;
    status?: string;
    payeePartyId?: number;
    payeeName?: string;
    currencyCode?: string;
    requestedAmount?: number;
    approvedAmount?: number;
    taxAmount?: number;
    linkedBillId?: number;
    linkedVoucherId?: number;
    paymentDueAt?: Date;
    submittedAt?: Date;
    submittedBy?: number;
    submittedByActor?: AuditActorSummary;
    approvedAt?: Date;
    approvedBy?: number;
    approvedByActor?: AuditActorSummary;
    rejectedAt?: Date;
    rejectedBy?: number;
    rejectedByActor?: AuditActorSummary;
    cancelledAt?: Date;
    cancelledBy?: number;
    cancelledByActor?: AuditActorSummary;
    convertedAt?: Date;
    convertedBy?: number;
    convertedByActor?: AuditActorSummary;
    decisionReason?: string;
    notes?: string;
    createdAt?: Date;
    updatedAt?: Date;
    lines?: CfShippingPaymentRequestLine[];
}

export interface CfRequestAction {
    reason?: string;
    approvedAmount?: number;
    billId?: number;
    voucherId?: number;
    actorId?: number;
}

export interface CfRequestWarning {
    code?: string;
    severity?: string;
    message?: string;
    requestKind?: string;
    requestId?: number;
    requestNumber?: string;
}

export interface CfRequestSummary {
    owningCompanyId?: number;
    logisticsProjectId?: number;
    supplierRequestedAmount?: number;
    supplierApprovedAmount?: number;
    paymentRequestedAmount?: number;
    paymentApprovedAmount?: number;
    pendingSupplierRequests?: number;
    approvedUnconvertedSupplierRequests?: number;
    pendingPaymentRequests?: number;
    approvedUnconvertedPaymentRequests?: number;
    supplierBookings?: CfSupplierBookingRequest[];
    shippingPayments?: CfShippingPaymentRequest[];
    warnings?: CfRequestWarning[];
}

export interface CfCloseRequest {
    closureReason?: string;
    closureComment?: string;
    override?: boolean;
    overrideReason?: string;
    actorId?: number;
}

export interface CfCloseReadiness {
    owningCompanyId?: number;
    logisticsProjectId?: number;
    cfExecutionId?: number;
    cfNumber?: string;
    reviewStatus?: string;
    blockingCount?: number;
    warningCount?: number;
    locked?: boolean;
    lockedAt?: Date;
    checks?: ProjectClosureCheck[];
    projectClosureReview?: ProjectClosureReview;
}

export interface CfCloseResult {
    project?: LogisticsProject;
    profile?: CfExecutionFile;
    readiness?: CfCloseReadiness;
}

export interface CfActivityActionRequest {
    actorId?: number;
    notes?: string;
    overrideCloseBlockers?: boolean;
    overrideReason?: string;
}

export interface CfApplyActivityTemplateRequest {
    actorId?: number;
    templateCode?: string;
    overwriteExisting?: boolean;
}

export interface CfExecutionWorkspace {
    project?: LogisticsProject;
    profile?: CfExecutionFile;
    clearances?: CfExecutionResourceSummary[];
    shipments?: CfExecutionResourceSummary[];
    deliveries?: CfExecutionResourceSummary[];
    containerCases?: CfExecutionResourceSummary[];
    activities?: CfExecutionActivity[];
    milestones?: ProjectMilestone[];
    documents?: ProjectDocument[];
    costSheet?: CfCostSheet;
    requests?: CfRequestSummary;
    financialSummary?: ProjectFinancialSummary;
    closureReadiness?: ProjectClosureReview;
    cfClosureReadiness?: CfCloseReadiness;
}

export interface ProjectRiskIssue {
    riskIssueId?: number;
    owningCompanyId?: number;
    projectId?: number;
    issueCode?: string;
    title?: string;
    description?: string;
    issueCategory?: string;
    issueType?: string;
    severity?: string;
    probability?: string;
    impactArea?: string;
    status?: string;
    sourceModule?: string;
    sourceResourceType?: string;
    sourceResourceId?: number;
    sourceReference?: string;
    sourceKey?: string;
    automated?: boolean;
    blockingClosure?: boolean;
    customerVisible?: boolean;
    ownerCustomerId?: number;
    ownerName?: string;
    dueAt?: Date;
    detectedAt?: Date;
    resolvedAt?: Date;
    resolvedBy?: number;
    resolutionComment?: string;
    overrideAllowed?: boolean;
    overrideApprovedAt?: Date;
    overrideApprovedBy?: number;
    overrideReason?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectClosureCheck {
    code?: string;
    label?: string;
    severity?: string;
    status?: string;
    blocking?: boolean;
    count?: number;
    message?: string;
}

export interface ProjectClosureReview {
    closureReviewId?: number;
    owningCompanyId?: number;
    projectId?: number;
    reviewStatus?: string;
    blockingCount?: number;
    warningCount?: number;
    overrideUsed?: boolean;
    overrideReason?: string;
    approvedBy?: number;
    approvedAt?: Date;
    checks?: ProjectClosureCheck[];
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectChangeRequest {
    changeRequestId?: number;
    owningCompanyId?: number;
    projectId?: number;
    requestType?: string;
    title?: string;
    description?: string;
    status?: string;
    requestedBy?: number;
    requestedAt?: Date;
    approvedBy?: number;
    approvedAt?: Date;
    rejectedBy?: number;
    rejectedAt?: Date;
    decisionComment?: string;
    impactSummary?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export const PROJECT_STATUSES = [
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Planned', value: 'PLANNED' },
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'On Hold', value: 'ON_HOLD' },
    { label: 'At Risk', value: 'AT_RISK' },
    { label: 'Completion Review', value: 'COMPLETION_REVIEW' },
    { label: 'Closed', value: 'CLOSED' },
    { label: 'Cancelled', value: 'CANCELLED' },
    { label: 'Archived', value: 'ARCHIVED' }
];

export const PROJECT_TYPES = [
    { label: 'General Logistics', value: 'GENERAL_LOGISTICS' },
    { label: 'Freight Forwarding & Customs', value: 'CLEARANCE_FORWARDING' },
    { label: 'C&F Import Air', value: 'CF_IMPORT_AIR' },
    { label: 'C&F Import Sea', value: 'CF_IMPORT_SEA' },
    { label: 'C&F Import Land', value: 'CF_IMPORT_LAND' },
    { label: 'C&F Export', value: 'CF_EXPORT' },
    { label: 'C&F RORO', value: 'CF_RORO' },
    { label: 'Transport Project', value: 'TRANSPORT_PROJECT' },
    { label: 'Container Project', value: 'CONTAINER_PROJECT' },
    { label: 'Clearance Project', value: 'CLEARANCE_PROJECT' },
    { label: 'Fleet Mobilization', value: 'FLEET_MOBILIZATION' },
    { label: 'Warehousing Project', value: 'WAREHOUSING_PROJECT' },
    { label: 'POSM Project', value: 'POSM_PROJECT' },
    { label: 'Contract Project', value: 'CONTRACT_PROJECT' },
    { label: 'Internal Project', value: 'INTERNAL_PROJECT' }
];

export const PROJECT_PRIORITIES = [
    { label: 'Low', value: 'LOW' },
    { label: 'Normal', value: 'NORMAL' },
    { label: 'High', value: 'HIGH' },
    { label: 'Urgent', value: 'URGENT' }
];

export const COMPLETION_METHODS = [
    { label: 'Manual', value: 'MANUAL' },
    { label: 'Milestone Weight', value: 'MILESTONE_WEIGHT' },
    { label: 'Task Completion', value: 'TASK_COMPLETION' },
    { label: 'Operational Events', value: 'OPERATIONAL_EVENTS' },
    { label: 'Financial Completion', value: 'FINANCIAL_COMPLETION' },
    { label: 'Hybrid', value: 'HYBRID' }
];

export const HEALTH_STATUSES = [
    { label: 'Green', value: 'GREEN' },
    { label: 'Amber', value: 'AMBER' },
    { label: 'Red', value: 'RED' },
    { label: 'Blocked', value: 'BLOCKED' },
    { label: 'Unknown', value: 'UNKNOWN' }
];

export const PROJECT_VISIBILITIES = [
    { label: 'Internal', value: 'INTERNAL' },
    { label: 'Customer Visible', value: 'CUSTOMER_VISIBLE' },
    { label: 'Supplier Visible', value: 'SUPPLIER_VISIBLE' },
    { label: 'Restricted', value: 'RESTRICTED' }
];

export const PROJECT_DOCUMENT_STATUSES = [
    { label: 'Missing', value: 'MISSING' },
    { label: 'Uploaded', value: 'UPLOADED' },
    { label: 'Verified', value: 'VERIFIED' },
    { label: 'Rejected', value: 'REJECTED' },
    { label: 'Waived', value: 'WAIVED' }
];

export const PROJECT_PARTICIPANT_TYPES = [
    { label: 'Customer', value: 'CUSTOMER' },
    { label: 'Supplier', value: 'SUPPLIER' },
    { label: 'Internal', value: 'INTERNAL' }
];

export const PROJECT_RISK_SEVERITIES = [
    { label: 'Low', value: 'LOW' },
    { label: 'Medium', value: 'MEDIUM' },
    { label: 'High', value: 'HIGH' },
    { label: 'Critical', value: 'CRITICAL' }
];

export const PROJECT_RISK_STATUSES = [
    { label: 'Open', value: 'OPEN' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Monitoring', value: 'MONITORING' },
    { label: 'Resolved', value: 'RESOLVED' },
    { label: 'Closed', value: 'CLOSED' },
    { label: 'Cancelled', value: 'CANCELLED' }
];

export const PROJECT_CHANGE_REQUEST_TYPES = [
    { label: 'Scope Change', value: 'SCOPE_CHANGE' },
    { label: 'Budget Change', value: 'BUDGET_CHANGE' },
    { label: 'Date Change', value: 'DATE_CHANGE' },
    { label: 'Customer Change', value: 'CUSTOMER_CHANGE' },
    { label: 'Contract / Rate Change', value: 'CONTRACT_RATE_CHANGE' }
];

// ── Resource Links ──────────────────────────────────────────

export interface ProjectResourceLink {
    linkId?: number; owningCompanyId?: number; projectId?: number;
    resourceType?: string; resourceId?: number; sourceModule?: string;
    relationType?: string; relationRole?: string; label?: string;
    status?: string; isPrimary?: boolean; sortOrder?: number;
    sourceStatus?: string; sourceReference?: string; sourceAmount?: number;
    currencyCode?: string; metadataJson?: string; linkedAt?: Date; linkedBy?: number;
    unlinkedAt?: Date; unlinkedBy?: number; unlinkReason?: string;
    createdAt?: Date; updatedAt?: Date;
}

export interface LinkableProjectResource {
    resourceType?: string;
    resourceId?: number;
    sourceModule?: string;
    sourceReference?: string;
    label?: string;
    sourceStatus?: string;
    sourceAmount?: number;
    currencyCode?: string;
    metadataJson?: string;
}

export interface ProjectFinancialFact {
    factId?: number;
    owningCompanyId?: number;
    projectId?: number;
    factType?: string;
    financialStage?: string;
    sourceModule?: string;
    sourceResourceType?: string;
    sourceResourceId?: number;
    sourceLineId?: number;
    sourceKey?: string;
    sourceDocumentNumber?: string;
    partyType?: string;
    partyId?: number;
    chargeType?: string;
    costType?: string;
    amount?: number;
    taxAmount?: number;
    currencyCode?: string;
    exchangeRate?: number;
    projectExchangeRate?: number;
    projectExchangeRateSourceCurrencyCode?: string;
    projectExchangeRateTargetCurrencyCode?: string;
    projectExchangeRateRecordedAt?: Date;
    projectExchangeRateRecordedBy?: number;
    amountCompanyCurrency?: number;
    eventDate?: Date;
    postingDate?: Date;
    status?: string;
    isReversal?: boolean;
    reversedFactId?: number;
    metadataJson?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface ProjectFinancialWarning {
    code?: string;
    severity?: string;
    title?: string;
    message?: string;
    sourceModule?: string;
    sourceResourceType?: string;
    sourceResourceId?: number;
}

export interface ProjectFinancialSummary {
    owningCompanyId?: number;
    projectId?: number;
    financialScope?: 'COMPANY' | 'GROUP';
    companyBreakdown?: ProjectFinancialSummary[];
    originalCurrencyTotals?: Record<string, number>;
    unconvertedFactCount?: number;
    asOfAt?: Date;
    estimatedRevenue?: number;
    estimatedCost?: number;
    orderedRevenue?: number;
    committedCost?: number;
    deliveredRevenue?: number;
    accruedCost?: number;
    invoicedRevenue?: number;
    billedCost?: number;
    collectedAmount?: number;
    paidAmount?: number;
    actualRevenue?: number;
    actualCost?: number;
    grossMargin?: number;
    grossMarginPercent?: number;
    wipAmount?: number;
    unbilledRevenue?: number;
    unbilledCost?: number;
    openArAmount?: number;
    openApAmount?: number;
    currencyCode?: string;
    calculationStatus?: string;
    calculationError?: string;
    updatedAt?: Date;
    posmLinked?: boolean;
    posmProjectId?: number;
    posmLinkError?: string;
    posmCosting?: PosmCostingSummary | null;
    facts?: ProjectFinancialFact[];
    warnings?: ProjectFinancialWarning[];
}

export interface ProjectReportWorkspace {
    projectId?: number;
    owningCompanyId?: number;
    sourceModule?: string;
    sourceProjectId?: number;
    availability?: PosmReportAvailability | null;
    completionStatus?: PosmClosureStatus | null;
    executionReports?: PosmExecutionReport[];
    completionReports?: PosmCompletionReport[];
    scheduleCompletionReports?: PosmScheduleCompletionReport[];
}

export interface ProjectExecutionReportGenerateRequest {
    reportType?: string;
    generatedByCustomerId?: number;
}

export interface ProjectCompletionReportGenerateRequest extends PosmGenerateCompletionReportRequest {}

export interface ProjectCompletionReportExport extends PosmReportExport {}

export interface ProjectCalendarEvent {
    eventId?: string;
    eventType?: string;
    title?: string;
    startAt?: Date;
    endAt?: Date;
    status?: string;
    resourceType?: string;
    resourceId?: number;
    requirementId?: number;
    sourceModule?: string;
    routeLabel?: string;
    severity?: string;
    deepLink?: string;
}

export const RESOURCE_TYPES = [
    { label: 'Shipment', value: 'SHIPMENT' },
    { label: 'Shipment Delivery', value: 'SHIPMENT_DELIVERY' },
    { label: 'Container Case', value: 'CONTAINER_CASE' },
    { label: 'Clearance File', value: 'CLEARANCE_HEADER' },
    { label: 'Clearance Container', value: 'CUSTOMS_CONTAINER' },
    { label: 'Clearance Procedure', value: 'CLEARANCE_PROCEDURE_CASE' },
    { label: 'Fleet Mobilization', value: 'FLEET_MOBILIZATION' },
    { label: 'Sales Order', value: 'SALES_ORDER' },
    { label: 'Purchase Order', value: 'PURCHASE_ORDER' },
    { label: 'Purchase Receipt', value: 'PURCHASE_RECEIPT' },
    { label: 'Sales Invoice', value: 'SALES_INVOICE' },
    { label: 'Payment Receipt', value: 'PAYMENT_RECEIPT' },
    { label: 'Vendor Bill', value: 'VENDOR_BILL' },
    { label: 'Expense Claim', value: 'EXPENSE_CLAIM' },
    { label: 'Stock Entry', value: 'STOCK_ENTRY' },
    { label: 'Warehouse Stock Entry', value: 'WAREHOUSING_STOCK_ENTRY' },
    { label: 'Warehouse Stock Movement', value: 'WAREHOUSING_STOCK_MOVEMENT' },
    { label: 'Warehouse Dispatch', value: 'WAREHOUSING_DISPATCH' },
    { label: 'Task', value: 'PRODUCTIVITY_TASK' },
    { label: 'Document', value: 'DOCUMENT' },
    { label: 'POSM Project', value: 'POSM_PROJECT' }
];

export const PROJECT_REQUIREMENT_TYPES = [
    { label: 'General Demand', value: 'GENERAL' },
    { label: 'C&F Job File', value: 'CF_JOB_FILE' },
    { label: 'C&F Shipment Leg', value: 'CF_SHIPMENT_LEG' },
    { label: 'C&F Clearance File', value: 'CF_CLEARANCE_FILE' },
    { label: 'C&F Container Case', value: 'CF_CONTAINER_CASE' },
    { label: 'C&F Empty Return', value: 'CF_EMPTY_RETURN' },
    { label: 'C&F Documentation', value: 'CF_DOCUMENTATION' },
    { label: 'C&F Duty / Tax', value: 'CF_DUTY_TAX' },
    { label: 'C&F Local Delivery', value: 'CF_LOCAL_DELIVERY' },
    { label: 'C&F Cost Sheet Line', value: 'CF_COST_ELEMENT' },
    { label: 'C&F Supplier Booking', value: 'CF_SUPPLIER_BOOKING' },
    { label: 'C&F Customer Billing', value: 'CF_CUSTOMER_BILLING' },
    { label: 'Transport Lane', value: 'TRANSPORT_LANE' },
    { label: 'Container Count', value: 'CONTAINER_COUNT' },
    { label: 'Container Return', value: 'CONTAINER_RETURN' },
    { label: 'Clearance Job', value: 'CLEARANCE_JOB' },
    { label: 'Customs Container', value: 'CUSTOMS_CONTAINER' },
    { label: 'Fleet Resource', value: 'FLEET_RESOURCE' },
    { label: 'Warehouse Storage', value: 'WAREHOUSE_STORAGE' },
    { label: 'Warehouse Dispatch', value: 'WAREHOUSE_DISPATCH' },
    { label: 'POSM Delivery', value: 'POSM_DELIVERY' },
    { label: 'POSM Installation', value: 'POSM_INSTALLATION' }
];

export const PROJECT_REQUIREMENT_TARGET_RESOURCE_TYPES = [
    { label: 'C&F Execution File', value: 'CF_EXECUTION' },
    { label: 'C&F Cost Element', value: 'CF_COST_ELEMENT' },
    { label: 'Shipment', value: 'SHIPMENT' },
    { label: 'Shipment Delivery', value: 'SHIPMENT_DELIVERY' },
    { label: 'Container Case', value: 'CONTAINER_CASE' },
    { label: 'Customs Container', value: 'CUSTOMS_CONTAINER' },
    { label: 'Clearance File', value: 'CLEARANCE_HEADER' },
    { label: 'Fleet Mobilization', value: 'FLEET_MOBILIZATION' },
    { label: 'Sales Order', value: 'SALES_ORDER' },
    { label: 'Purchase Order', value: 'PURCHASE_ORDER' },
    { label: 'Sales Invoice', value: 'SALES_INVOICE' },
    { label: 'Vendor Bill', value: 'VENDOR_BILL' },
    { label: 'Payment Receipt', value: 'PAYMENT_RECEIPT' },
    { label: 'Vendor Voucher', value: 'VENDOR_VOUCHER' },
    { label: 'Warehouse Stock Entry', value: 'WAREHOUSING_STOCK_ENTRY' },
    { label: 'Warehouse Stock Movement', value: 'WAREHOUSING_STOCK_MOVEMENT' },
    { label: 'Warehouse Dispatch', value: 'WAREHOUSING_DISPATCH' },
    { label: 'POSM Project', value: 'POSM_PROJECT' }
];

export const PROJECT_REQUIREMENT_STATUSES = [
    { label: 'Open', value: 'OPEN' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Blocked', value: 'BLOCKED' },
    { label: 'Cancelled', value: 'CANCELLED' }
];

export const PROJECT_REQUIREMENT_SOURCING_MODES = [
    { label: 'In House', value: 'IN_HOUSE' },
    { label: 'Supplier', value: 'SUPPLIER' },
    { label: 'Mixed', value: 'MIXED' }
];

export const RELATION_TYPES = [
    { label: 'Fulfills', value: 'FULFILLS' },
    { label: 'Funded By', value: 'FUNDED_BY' },
    { label: 'Billed By', value: 'BILLED_BY' },
    { label: 'Costed By', value: 'COSTED_BY' },
    { label: 'Primary Context', value: 'PRIMARY_CONTEXT' },
    { label: 'Originated From', value: 'ORIGINATED_FROM' },
    { label: 'Evidence', value: 'EVIDENCE' },
    { label: 'Related', value: 'RELATED' },
    { label: 'Blocked By', value: 'BLOCKED_BY' },
    { label: 'Task', value: 'TASK' }
];

export interface ProjectEvent {
    eventId?: number; owningCompanyId?: number; projectId?: number;
    eventCode?: string; eventCategory?: string; eventSeverity?: string;
    eventSource?: string; sourceModule?: string;
    sourceEntityType?: string; sourceEntityId?: number; sourceReference?: string;
    eventTime?: Date; actorCustomerId?: number; actor?: AuditActorSummary; actorName?: string;
    locationId?: number; locationText?: string; message?: string; metadataJson?: string; createdAt?: Date;
}

export const EVENT_CATEGORIES: Record<string, { icon: string; color: string }> = {
    SYSTEM: { icon: 'pi pi-cog', color: '#6b7280' },
    STATUS: { icon: 'pi pi-flag', color: '#3b82f6' },
    LINK_ADDED: { icon: 'pi pi-link', color: '#22c55e' },
    LINK_REMOVED: { icon: 'pi pi-unlink', color: '#ef4444' },
    SHIPMENT: { icon: 'pi pi-truck', color: '#8b5cf6' },
    DELIVERY: { icon: 'pi pi-box', color: '#f59e0b' },
    CONTAINER: { icon: 'pi pi-box', color: '#06b6d4' },
    CLEARANCE: { icon: 'pi pi-shield', color: '#f97316' },
    FLEET: { icon: 'pi pi-car', color: '#14b8a6' },
    FINANCE: { icon: 'pi pi-money-bill', color: '#10b981' },
    DOCUMENT: { icon: 'pi pi-file', color: '#6366f1' },
    EXCEPTION: { icon: 'pi pi-exclamation-triangle', color: '#dc2626' },
    COMMENT: { icon: 'pi pi-comment', color: '#a855f7' }
};
